import type { ResultadosDeAutenticacao } from './sinaisDeAutenticacao';
import { campoDoErro, textoDaApi } from './texto-da-entrada';
/**
 * Cliente da API.
 *
 * O access token vive só em memória, nunca em `localStorage`: XSS que rode
 * nesta origem não consegue lê-lo. Quem sobrevive ao recarregamento é o cookie
 * de refresh, que é `HttpOnly` e o JavaScript não enxerga (Seção 15.3).
 */

import type { AcoesDaRegra, CondicoesDaRegra } from '@waymail/core';

const BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3451';

/** Origem da API, para o que nao passa por `api()` - a imagem dentro do corpo, por exemplo. */
export const ORIGEM_DA_API = BASE;

let accessToken: string | null = null;
let usuario: Usuario | null = null;
let aoPerderSessao: (() => void) | null = null;

export function definirToken(token: string | null, dono?: Usuario): void {
  accessToken = token;
  if (dono !== undefined) usuario = dono;
  if (token === null) usuario = null;
}

/** Quem está logado agora, segundo a última resposta de sessão. */
export function usuarioAtual(): Usuario | null {
  return usuario;
}

export function temToken(): boolean {
  return accessToken !== null;
}

export function aoExpirar(callback: () => void): void {
  aoPerderSessao = callback;
}

export class ErroDaApi extends Error {
  /** Campo do body que a API apontou (prefixo `body/<campo>` do zod), se houver. */
  readonly campo: string | undefined;

  constructor(
    readonly status: number,
    mensagem: string,
  ) {
    // Texto de tela: pt-BR e acentuado, uma vez, para toda tela que mostra `message`.
    super(textoDaApi(mensagem));
    this.name = 'ErroDaApi';
    this.campo = campoDoErro(mensagem);
  }
}

interface Opcoes {
  method?: string;
  body?: unknown;
  /** Interno: evita laço quando o próprio refresh devolve 401. */
  semRenovar?: boolean;
}

async function bruto(caminho: string, opcoes: Opcoes = {}): Promise<Response> {
  const { method = 'GET', body } = opcoes;

  return fetch(`${BASE}/api/v1${caminho}`, {
    method,
    // O cookie de refresh precisa ir junto nas rotas de auth.
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(accessToken !== null ? { authorization: `Bearer ${accessToken}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

export async function api<T>(caminho: string, opcoes: Opcoes = {}): Promise<T> {
  let resposta = await bruto(caminho, opcoes);

  // Access token expira em 15 min; renovar é rotina, não erro.
  if (resposta.status === 401 && opcoes.semRenovar !== true) {
    const renovou = await renovar();
    if (renovou) {
      resposta = await bruto(caminho, opcoes);
    } else {
      accessToken = null;
      aoPerderSessao?.();
    }
  }

  if (resposta.status === 204) return undefined as T;

  const texto = await resposta.text();
  const dados: unknown = texto === '' ? null : JSON.parse(texto);

  if (!resposta.ok) {
    const mensagem =
      typeof dados === 'object' && dados !== null && 'error' in dados
        ? String((dados as { error: unknown }).error)
        : 'Não foi possível concluir a operação.';
    throw new ErroDaApi(resposta.status, mensagem);
  }

  return dados as T;
}

let renovando: Promise<boolean> | null = null;

/**
 * Renova o access token. Devolve `false` quando o servidor recusa e **lança**
 * quando não há rede - são coisas diferentes: a primeira encerra a sessão, a
 * segunda só adia.
 *
 * Uma renovação de cada vez. O refresh token roda a cada uso e o servidor trata
 * reuso como ataque, derrubando a família inteira de sessões (Seção 15.3). Duas
 * requisições expirando juntas - o que acontece sempre que a rede volta - se
 * transformariam em logout forçado sem isto.
 */
export function renovar(): Promise<boolean> {
  renovando ??= renovacao().finally(() => {
    renovando = null;
  });
  return renovando;
}

async function renovacao(): Promise<boolean> {
  const resposta = await bruto('/auth/refresh', { method: 'POST', semRenovar: true });
  if (!resposta.ok) return false;

  const dados = (await resposta.json()) as Sessao;
  accessToken = dados.accessToken;
  usuario = dados.user;
  return true;
}

/**
 * Encerra a sessão no servidor. Sem isto o cookie de refresh continua válido e
 * recarregar a página traz a pessoa de volta para dentro, sem senha.
 */
export async function sair(): Promise<void> {
  try {
    await api('/auth/logout', { method: 'POST', semRenovar: true });
  } catch {
    // Sem rede o cookie morre só quando expirar. Localmente a sessão acaba aqui.
  }
  accessToken = null;
  usuario = null;
}

// Tipos das respostas -------------------------------------------------------

export interface Usuario {
  id: string;
  email: string;
  name: string;
  totpEnabled: boolean;
}

export interface Sessao {
  accessToken: string;
  expiresIn: number;
  user: Usuario;
}

export interface Conta {
  id: string;
  email: string;
  displayName: string;
  /** Data URL da foto da caixa. Opcional: o cache offline pode ser antigo. */
  photo?: string | null;
  provider: string;
  authType: string;
  imapHost: string | null;
  syncState: string;
  lastSyncAt: string | null;
  lastError: string | null;
  /**
   * O que a barra lateral mostra (ADR 0053). Opcional: a lista pode vir do
   * cache offline gravado por uma versao que ainda nao tinha o campo.
   */
  syncStatus?: EstadoDaSincronizacao;
}

export type EstadoDaSincronizacao = 'em_dia' | 'sincronizando' | 'sem_conexao' | 'credencial';

export interface Pasta {
  id: string;
  path: string;
  name: string;
  role: string | null;
  unreadCount: number;
  /*
   * Gestao de pastas (ADR 0045). Opcionais porque a lista pode vir do cache
   * offline gravado por uma versao anterior, que nao tinha estes campos.
   */
  /** O que o servidor indicou. `role` e o que vale, com a escolha do usuario. */
  detectedRole?: string | null;
  roleOverride?: string | null;
  /** Separador de hierarquia do servidor: `.` no cPanel, `/` no resto. */
  delimiter?: string | null;
  subscribed?: boolean;
}

export interface ConversaDaApi {
  id: string;
  messageId: string;
  accountId: string;
  subject: string;
  participants: string[];
  messageCount: number;
  unread: boolean;
  flagged: boolean;
  hasAttachment: boolean;
  lastMessageAt: string;
  snippet: string | null;
}

export interface PaginaDeConversas {
  items: ConversaDaApi[];
  nextCursor: string | null;
}

export interface MensagemDaApi {
  id: string;
  uid: number;
  accountId: string;
  from: { name: string; address: string } | null;
  to: { name: string; address: string }[];
  cc: { name: string; address: string }[];
  replyTo: { name: string; address: string }[];
  subject: string | null;
  snippet: string | null;
  sentAt: string;
  flags: string[];
  hasAttachment: boolean;
  /** Message-ID da RFC 5322, nao o id do banco. */
  rfcMessageId: string | null;
  references: string[];
  attachments: AnexoDaApi[];
  bodyText: string | null;
  /** SPF/DKIM/DMARC do servidor receptor; `null` = desconhecido (ADR 0051). */
  authResults?: ResultadosDeAutenticacao | null;
  /** Convite de reuniao (.ics) lido no servidor. Ausente em cache de versao anterior. */
  convite?: ConviteDaApi | null;
}

/** Espelho de `conviteSchema` em `services/api/src/lib/convite.ts`. */
export interface ConviteDaApi {
  uid: string;
  metodo: 'REQUEST' | 'CANCEL' | 'REPLY' | 'PUBLISH';
  titulo: string;
  local: string | null;
  descricao: string | null;
  organizador: { nome: string; email: string | null } | null;
  /** ISO 8601: com `Z` e instante, sem fuso e hora local, so data e dia inteiro. */
  inicio: string;
  fim: string | null;
  diaInteiro: boolean;
  repete: boolean;
  /** A repeticao cabe no subconjunto e entra como serie (ADR 0071). */
  serie: boolean;
  fusoNaoReconhecido: boolean;
  jaNaAgenda: boolean;
}

/** Espelho de `preferenciasSchema` em `services/api/src/routes/settings.ts`. */
export interface Preferencias {
  leitura: {
    /** `null` e nunca marcar sozinho. */
    marcarLidaAposMs: number | null;
    imagensSempreDe: string[];
  };
  redacao: {
    formato: 'html' | 'texto';
    responderAcima: boolean;
    /** Segundos de janela do Desfazer envio. 0 envia na hora. */
    atrasoDeEnvio: number;
  };
  atalhos: {
    /** Atalhos de uma tecla. Ctrl/Cmd+K vale mesmo desligado. */
    ativos: boolean;
  };
}

/** Busca salva do usuario (ADR 0050); `accountId` nulo vale para todas as caixas. */
export interface BuscaSalvaDaApi {
  id: string;
  name: string;
  query: string;
  accountId: string | null;
  /** Pasta inteligente (WYM-12): vista virtual, nao busca salva. */
  smart: boolean;
  createdAt: string;
}

/** Assinatura de uma caixa (ADR 0044). O HTML ja vem sanitizado do servidor. */
export interface AssinaturaDaApi {
  id: string;
  accountId: string;
  name: string;
  html: string;
  /** Entra nas mensagens novas desta caixa. */
  isDefault: boolean;
  /** Entra nas respostas e encaminhamentos desta caixa. */
  isReplyDefault: boolean;
}

/** Resposta pronta do usuario (ADR 0044), para qualquer caixa. */
export interface RespostaProntaDaApi {
  id: string;
  name: string;
  html: string;
}

export interface Contato {
  name: string;
  address: string;
  /** Quantas mensagens trocadas: e o que ordena a sugestao. */
  frequency: number;
}

/** Contato salvo na agenda (ADR 0042), diferente da sugestao derivada acima. */
export interface ContatoSalvo {
  id: string;
  name: string | null;
  email: string;
  emails: { type: string | null; value: string }[];
  phones: { type: string | null; value: string }[];
  organization: string | null;
  jobTitle: string | null;
  notes: string | null;
  source: string | null;
  updatedAt: string;
}

export interface PaginaDeContatos {
  items: ContatoSalvo[];
  total: number;
}

export interface ResultadoDaImportacao {
  formato: 'vcard' | 'csv';
  criados: number;
  atualizados: number;
  ignorados: number;
}

/** Regra da Entrada. `conditions` e `actions` seguem o `Regra` do core. */
export interface RegraDaApi {
  id: string;
  accountId: string;
  name: string;
  enabled: boolean;
  priority: number;
  conditions: CondicoesDaRegra;
  actions: AcoesDaRegra;
  stopProcessing: boolean;
}

export interface AnexoDaApi {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  isInline: boolean;
  /** Extensao executavel (Secao 15.6): a tela confirma antes de baixar. */
  bloqueado: boolean;
}

export type CategoriaDeAnexo = 'pdf' | 'imagem' | 'planilha' | 'documento' | 'compactado' | 'outro';

/** Um anexo na Central (WYM-13): metadados e a mensagem de onde veio. */
export interface AnexoDaCentral {
  id: string;
  messageId: string;
  accountId: string;
  folderId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  bloqueado: boolean;
  categoria: CategoriaDeAnexo;
  receivedAt: string;
  subject: string | null;
  from: { name: string; address: string } | null;
}

export interface PaginaDeAnexos {
  items: AnexoDaCentral[];
  nextCursor: string | null;
}

export interface CorpoDaApi {
  html: string;
  imagensRemotasBloqueadas: number;
  rastreadoresRemovidos: number;
  linksSuspeitos: string[];
}

export interface Servidor {
  host: string;
  port: number;
  security: 'ssl' | 'starttls' | 'none';
}

export interface ConfiguracaoDescoberta {
  imap: Servidor;
  smtp: Servidor;
  authType: 'password' | 'oauth2';
  origem: string;
  confirmado: boolean;
}

/** Painel cPanel ou Plesk conectado pelo usuário (ADR 0039). O segredo nunca volta. */
export interface PainelDeHospedagem {
  id: string;
  type: 'cpanel' | 'plesk';
  host: string;
  port: number;
  username: string;
  authMode: 'token' | 'password';
  label: string | null;
  createdAt: string;
}

export interface CaixaDoPainel {
  email: string;
  domain: string;
  quotaMb: number | null;
  usedMb: number | null;
  inWayMail: boolean;
}

export interface CaixasDoPainel {
  /** `max` nulo é plano ilimitado. */
  usage: { used: number; max: number | null };
  mailboxes: CaixaDoPainel[];
}

export interface Encaminhamento {
  source: string;
  destination: string;
}

export interface RespostaAutomatica {
  email: string;
  subject: string;
  body: string | null;
}

/** Ausência e encaminhamento da própria caixa (ADR 0046). */
export type EstrategiaDoFiltro = 'managesieve' | 'cpanel-webmail';

export interface Ausencia {
  enabled: boolean;
  subject: string;
  body: string;
  startDate: string | null;
  endDate: string | null;
  days: number;
}

export interface EncaminhamentoDaCaixa {
  enabled: boolean;
  address: string;
  keepCopy: boolean;
}

interface FiltroDaCaixa {
  strategy: EstrategiaDoFiltro | null;
  capabilities: { dates: boolean; optionalCopy: boolean } | null;
  reason: string | null;
  panels: { id: string; type: 'cpanel' | 'plesk'; host: string; label: string | null }[];
}

export interface AusenciaDaConta extends FiltroDaCaixa {
  vacation: Ausencia | null;
}

export interface EncaminhamentoDaConta extends FiltroDaCaixa {
  forwarding: EncaminhamentoDaCaixa | null;
}

export interface CaixaCriada {
  email: string;
  wayMailAccountId: string | null;
  wayMailError: string | null;
}

export interface ArquivoBaixado {
  blob: Blob;
  /** Tipo que a API declarou, sem parametros. */
  tipo: string;
  /** Os bytes desmentem o tipo ou o nome: nao se mostra, so se oferece baixar (ADR 0051). */
  divergente: boolean;
}

/**
 * Busca um arquivo da API com o token. Nao da para usar um link simples:
 * a API e outra origem e o access token vive em memoria, entao o link sairia
 * sem Authorization e voltaria 401.
 */
export async function buscarArquivo(caminho: string): Promise<ArquivoBaixado> {
  let resposta = await bruto(caminho);

  if (resposta.status === 401) {
    const renovou = await renovar();
    if (renovou) {
      resposta = await bruto(caminho);
    } else {
      accessToken = null;
      aoPerderSessao?.();
    }
  }

  if (!resposta.ok) {
    const texto = await resposta.text();
    let mensagem = 'Não foi possível baixar o arquivo.';
    try {
      const dados: unknown = JSON.parse(texto);
      if (typeof dados === 'object' && dados !== null && 'error' in dados) {
        mensagem = String((dados as { error: unknown }).error);
      }
    } catch {
      // Corpo que nao e JSON: fica a mensagem padrao.
    }
    throw new ErroDaApi(resposta.status, mensagem);
  }

  return {
    blob: await resposta.blob(),
    tipo: (resposta.headers.get('content-type') ?? '').split(';')[0]!.trim().toLowerCase(),
    divergente: resposta.headers.get('x-waymail-tipo-divergente') === 'sim',
  };
}

/** Baixa um arquivo da API e entrega ao navegador: busca autenticada, blob, e so entao o salvar. */
export async function baixarArquivo(caminho: string, nome: string): Promise<void> {
  const { blob } = await buscarArquivo(caminho);
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();
  } finally {
    // Sem revogar, o blob fica na memória da aba até ela fechar.
    URL.revokeObjectURL(url);
  }
}
